import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { ContentItemHomeSuperSplashLargePrimaryV2 } from "scmp-app/components/content/content-item-render/variants/home-super-splash-large-primary-v2";
import { ContentItemHomeSuperSplashLargeSecondaryV2 } from "scmp-app/components/content/content-item-render/variants/home-super-splash-large-secondary-v2";
import type { superSplashLargeHomeV2LiveWidget$key } from "scmp-app/queries/__generated__/superSplashLargeHomeV2LiveWidget.graphql";

import { Container, Label, SecondaryArticles } from "./styles";

type Props = {
  className?: string;
  reference: superSplashLargeHomeV2LiveWidget$key;
};

export const HomeV2SuperSplashLarge: FunctionComponent<Props> = ({ className, reference }) => {
  const data = useFragment(
    graphql`
      fragment superSplashLargeHomeV2LiveWidget on LiveWidget {
        label
        relatedArticles {
          entityId
          ...homeSuperSplashLargePrimaryV2ContentItemContent
          ...homeSuperSplashLargeSecondaryV2ContentItemContent
        }
      }
    `,
    reference,
  );

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const label = data?.label;
  const [firstArticle, secondArticle, thirdArticle] = data?.relatedArticles ?? [];
  const superSplashSecondaryArticles = [secondArticle, thirdArticle].filter(notEmpty);

  const hasSecondaryArticles = superSplashSecondaryArticles.length > 0;
  const shouldShowSubHeadline = superSplashSecondaryArticles.length === 1;

  const renderSecondaryArticles = (articles: typeof superSplashSecondaryArticles) =>
    articles.map(
      article =>
        article && (
          <ContentItemHomeSuperSplashLargeSecondaryV2
            key={article.entityId}
            reference={article}
            withComment={isMobile}
            withSubHeadline={shouldShowSubHeadline}
          />
        ),
    );

  return (
    <Container
      $articleCount={superSplashSecondaryArticles.length + 1}
      $hasLabel={!!label}
      className={className}
    >
      {label && <Label>{label}</Label>}
      {firstArticle && <ContentItemHomeSuperSplashLargePrimaryV2 reference={firstArticle} />}
      {hasSecondaryArticles && (
        <SecondaryArticles>
          {renderSecondaryArticles(superSplashSecondaryArticles)}
        </SecondaryArticles>
      )}
    </Container>
  );
};

HomeV2SuperSplashLarge.displayName = "HomeV2SuperSplashLarge";
